import { useState } from "react";
import { ProbateDetails } from "../../../models/ProbateDetails";
import { Box, Button, Typography } from "@mui/material";

interface Props {
  probateDetails?: ProbateDetails;
}

export const ProbateKnowledgeEngineDebugger = ({
  probateDetails,
}: Props): JSX.Element => {
  // console.log("fieldsAnswered: ", fieldsAnswered);
  const [showDebugger, setShowDebugger] = useState<boolean>(false);
  // console.log("probateDetails in debugger=", probateDetails);

  const getFieldWithAnswer = (fieldId: string) => {
    const thisFieldWithAnswer = probateDetails?.fieldsUsed?.find(
      (thisField) => {
        return thisField.id === fieldId;
      }
    );
    return thisFieldWithAnswer;
  };

  /**
   * Generate a message describing the "passed_conditional" result for a probate type definition.
   */
  const getConditionalPassedMessage = (
    passedConditional: boolean,
    thisProbateTypeMapping: any,
    fieldsNeededIds: Array<string>
  ): string => {
    let msg = "";
    if (passedConditional === true) {
      msg = "passed check(s)";
    } else if (passedConditional === false) {
      msg = "did not pass check(s)";
    } else if (passedConditional === null) {
      if (thisProbateTypeMapping !== "Full") {
        if (fieldsNeededIds.length > 0) {
          msg = `can't check yet, see "Next required questions"`;
        } else if (fieldsNeededIds.length === 0) {
          msg = `didn't check, maybe qualified for another probate type already?`;
        }
      } else if (thisProbateTypeMapping === "Full") {
        msg = "not qualified, see other probate types";
      }
    }
    return msg;
  };

  const renderQualifyingProbateType = (): JSX.Element => {
    return (
      <Box
        sx={{
          margin: "10px 0 0 0",
          pl: 2,
          pb: 0,
          pr: 2,
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: "18px",
            textAlign: "center",
            color: probateDetails?.probateType ? "green" : "#d00101",
          }}
        >
          {probateDetails?.probateType
            ? "Qualified! " +
              " (" +
              probateDetails?.probateType.name +
              ", id: " +
              probateDetails?.probateType?.id +
              ")"
            : "No qualifying probate type determined"}
        </Typography>
      </Box>
    );
  };

  const renderFieldsNeeded = (): JSX.Element => {
    return (
      <Box
        sx={{
          border: "3px solid orange",
          margin: "10px 0 0 0",
          pl: 2,
          pb: 2,
          pr: 2,
        }}
      >
        <h3
          style={{
            margin: "10px 0 0 0",
          }}
        >
          Next required question(s)
        </h3>
        {probateDetails?.fieldsNeededIds?.length === 0 ? (
          "All necessary questions answered!"
        ) : (
          <ul
            style={{
              margin: 0,
            }}
          >
            {probateDetails?.fieldsNeededIds?.map((thisFieldIdNeeded) => {
              const thisField = getFieldWithAnswer(thisFieldIdNeeded);
              return <li key={thisFieldIdNeeded}>{thisField?.question}</li>;
            })}
          </ul>
        )}
      </Box>
    );
  };

  const renderConditional = (
    conditional: any,
    index = 0,
    depth = 1,
    thisProbateTypeDefinition: any = null
  ): JSX.Element => {
    // console.log('-- got conditional', JSON.stringify(conditional, null, 2));
    const thisFieldWithAnswer = conditional.field_id
      ? getFieldWithAnswer(conditional.field_id)
      : null;
    // console.log('thisFieldWithAnswer=', (dev only) thisFieldWithAnswer);
    return (
      <Box
        key={
          conditional.type +
          "-" +
          index +
          "-" +
          conditional.passed_conditional +
          "-" +
          conditional.field_id +
          "-" +
          conditional.argument
        }
        sx={{
          width: "100%",
          paddingLeft: `${(depth || 1) * 12}px`,
          pr: 1,
          py: 1,
          my: 1,
          border: "2px solid",
          borderColor: "secondary.main", // atticus purple
          borderRadius: 1,
        }}
      >
        <Typography
          sx={{
            color: conditional.passed_conditional
              ? "primary.main"
              : "error.main",
          }}
        >
          {getConditionalPassedMessage(
            conditional.passed_conditional,
            thisProbateTypeDefinition?.type_mapping,
            (probateDetails && probateDetails.fieldsNeededIds) || []
          )}
        </Typography>

        <Typography>
          {thisFieldWithAnswer ? (
            <span>
              <strong>Question:</strong> {thisFieldWithAnswer.question}
            </span>
          ) : null}
          {thisFieldWithAnswer ? (
            <Typography
              variant="body2"
              component="span"
              sx={{
                pl: 1,
                color: "gray",
              }}
            >
              ({conditional.field_id})
            </Typography>
          ) : null}
        </Typography>
        <Typography>
          {thisFieldWithAnswer ? (
            <span>
              <strong>Answer:</strong>{" "}
              {thisFieldWithAnswer.answerValue?.toString()}
            </span>
          ) : null}
        </Typography>

        <Typography>
          <strong>Conditional type:</strong> {conditional.type}
          {conditional.type === "or" ? "  (ANY child must pass)" : null}
          {conditional.type === "and" ? "  (ALL children must pass)" : null}
          {conditional.type === "gather_data"
            ? "  (no check, just asks question)"
            : null}
          {conditional.type === "noop" ? "  (no check)" : null}
          {conditional.argument ? (
            <span style={{ color: "#3065e1" }}> ({conditional.argument})</span>
          ) : null}
        </Typography>

        {conditional.conditionals
          ? conditional.conditionals.map(
              (thisChildConditional: any, index: number) => {
                return renderConditional(
                  thisChildConditional,
                  index,
                  depth + 1
                );
              }
            )
          : null}
      </Box>
    );
  };

  return (
    <Box
      className="ProbateKnowledgeEngineDebugger"
      sx={{
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          sx={{
            width: "300px",
            mb: 1,
          }}
          onClick={() => setShowDebugger(!showDebugger)}
        >
          {showDebugger ? "Hide" : "Show"} probate debugger
        </Button>
      </div>
      {showDebugger ? (
        <div>
          {renderQualifyingProbateType()}
          {renderFieldsNeeded()}
          {probateDetails?.probateTypeDefinitions?.map(
            (thisProbateTypeDef, probateIndex) => (
              <ProbateTypeDefinition
                thisProbateTypeDefinition={thisProbateTypeDef}
                probateDetails={probateDetails}
                getConditionalPassedMessage={getConditionalPassedMessage}
                renderConditional={renderConditional}
                index={probateIndex}
              />
            )

            // {
            //   return renderProbateTypeDefinition(
            //     thisProbateTypeDef,
            //     probateDetails,
            //     getConditionalPassedMessage,
            //     renderConditional,
            //     probateIndex,
            //   );
            // }
          )}
        </div>
      ) : (
        []
      )}
    </Box>
  );
};

const ProbateTypeDefinition = (props: {
  thisProbateTypeDefinition: any;
  probateDetails: any;
  getConditionalPassedMessage: (
    type: any,
    type_mapping: any,
    conditionals: any
  ) => any;
  renderConditional: (
    conditional: any,
    index?: number,
    depth?: number,
    thisProbateTypeDefinition?: any
  ) => any;
  index: number; // just for the key
}): JSX.Element => {
  const [showThisProbateType, setShowThisProbateType] =
    useState<boolean>(false);
  return (
    <Box
      key={props.thisProbateTypeDefinition.type + "-" + props.index}
      sx={{
        border: "solid",
        borderRadius: 1,
        mt: 1, // 8px
        mx: 0, // left and right
        mb: 2, // 16px
        p: 1, // all sides
        paddingLeft: "12px",
        backgroundColor: "background.default",
      }}
    >
      <Typography
        variant="h3"
        gutterBottom={true}
        sx={{
          fontSize: "20px",
        }}
      >
        {props.thisProbateTypeDefinition.type} (
        {props.thisProbateTypeDefinition.type_mapping})
      </Typography>
      <Typography
        sx={{
          color: props.thisProbateTypeDefinition.conditionals.passed_conditional
            ? "primary.main"
            : "error.main",
        }}
      >
        {props.getConditionalPassedMessage(
          props.thisProbateTypeDefinition.conditionals.passed_conditional,
          props.thisProbateTypeDefinition?.type_mapping,
          (props.probateDetails && props.probateDetails.fieldsNeededIds) || []
        )}
      </Typography>
      <Button
        variant="contained"
        sx={{
          mt: 2,
        }}
        onClick={() => setShowThisProbateType(!showThisProbateType)}
      >
        {showThisProbateType ? "Hide" : "Show"} this probate definition
      </Button>

      {showThisProbateType && props.thisProbateTypeDefinition?.conditionals
        ? props.renderConditional(
            props.thisProbateTypeDefinition.conditionals,
            0,
            0,
            props.thisProbateTypeDefinition
          )
        : null}
    </Box>
  );
};
